/**
 * useLogger — Central structured logging composable for the frontend.
 *
 * Philosophy
 * ----------
 * - In DEVELOPMENT: all levels are emitted to the browser console with
 *   structured context objects so devtools are useful.
 * - In PRODUCTION: DEBUG/INFO are silenced. WARN and ERROR are emitted
 *   to the console (for browser error monitoring tools like Sentry) and
 *   can optionally be sent to a backend endpoint for server-side aggregation.
 * - NEVER shows raw exception objects or stack traces to users.
 *   Presentable user-facing messages are the concern of the calling code
 *   (stores / components) using the notification system.
 *
 * Usage
 * -----
 *   const logger = useLogger('cart')         // module name
 *   logger.info('Cart loaded', { items: 3 })
 *   logger.error('Checkout failed', err)
 *
 * Log levels (ascending severity)
 *   debug  → development only, very verbose
 *   info   → normal operational events
 *   warn   → recoverable unexpected states
 *   error  → something failed that the user will notice
 */

const IS_DEV = import.meta.env.DEV;
const IS_TEST = import.meta.env.MODE === 'test';

// ── Level constants ───────────────────────────────────────────────────────────
const LEVELS = { debug: 0, info: 1, warn: 2, error: 3 };
const MIN_LEVEL = IS_DEV || IS_TEST ? LEVELS.debug : LEVELS.warn;

// ── Remote log endpoint (optional) ───────────────────────────────────────────
// Set VITE_LOG_ENDPOINT=/api/client-logs/ in .env to enable remote error reporting.
const REMOTE_ENDPOINT = import.meta.env.VITE_LOG_ENDPOINT || null;
const REMOTE_MIN_LEVEL = LEVELS.error; // only ship errors to the server

// ── Formatting ────────────────────────────────────────────────────────────────
const _ts = () => new Date().toISOString();

const _fmt = (level, module, message) =>
  `[${_ts()}] [${level.toUpperCase()}] [${module}] ${message}`;

// ── Remote shipping ───────────────────────────────────────────────────────────
const _sendRemote = (level, module, message, context) => {
  if (!REMOTE_ENDPOINT) return;
  if (LEVELS[level] < REMOTE_MIN_LEVEL) return;

  // Best-effort POST; failures are silently ignored so logging never breaks the app
  try {
    const body = JSON.stringify({
      level,
      module,
      message,
      context: context ? _safeSerialize(context) : undefined,
      ts: _ts(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    });

    // Use sendBeacon for reliability on page unload; fall back to fetch
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(REMOTE_ENDPOINT, blob);
    } else {
      fetch(REMOTE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {}); // intentional no-op
    }
  } catch {
    // never let logging crash the app
  }
};

// ── Safe serialization ────────────────────────────────────────────────────────
// Converts errors and circular references gracefully.
const _safeSerialize = (value) => {
  if (value instanceof Error) {
    return { message: value.message, name: value.name };
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return String(value);
  }
};

// ── Core emit function ────────────────────────────────────────────────────────
const _emit = (level, module, message, context) => {
  if (LEVELS[level] < MIN_LEVEL) return;

  const formatted = _fmt(level, module, message);

  // eslint-disable-next-line no-console
  const consoleFn = console[level] || console.log;

  if (context !== undefined) {
    consoleFn(formatted, context);
  } else {
    consoleFn(formatted);
  }

  _sendRemote(level, module, message, context);
};

// ── Composable ────────────────────────────────────────────────────────────────
/**
 * Create a logger bound to a module name.
 *
 * @param {string} moduleName  - e.g. 'cart', 'auth', 'checkout'
 * @returns {{ debug, info, warn, error }}
 */
export function useLogger(moduleName = 'app') {
  return {
    /**
     * Verbose internal events — only shown in development.
     * @param {string} message
     * @param {*} [context]
     */
    debug(message, context) {
      _emit('debug', moduleName, message, context);
    },

    /**
     * Normal operational events.
     * @param {string} message
     * @param {*} [context]
     */
    info(message, context) {
      _emit('info', moduleName, message, context);
    },

    /**
     * Unexpected but recoverable situation.
     * @param {string} message
     * @param {*} [context]
     */
    warn(message, context) {
      _emit('warn', moduleName, message, context);
    },

    /**
     * Something failed that the user is likely to notice.
     * @param {string} message
     * @param {Error|*} [errorOrContext]
     */
    error(message, errorOrContext) {
      _emit('error', moduleName, message, errorOrContext);
    },
  };
}

export default useLogger;
